import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarClock, Clock3, User } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/Dialog';
import { Avatar } from '@/shared/ui/Avatar';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DatePicker } from '@/shared/ui/DatePicker';
import { Input } from '@/shared/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/lib/toast';
import { markAsHandled } from '@/lib/sentry';
import { cn } from '@/shared/lib/utils';
import { useClinicProfile } from '@/features/settings';
import { useSession } from '@/features/auth';

import { AppointmentTimeWindow } from './AppointmentTimeWindow';
import { DayAppointmentsPanel } from './DayAppointmentsPanel';
import { ScheduleAvailabilityWarning } from './ScheduleAvailabilityWarning';
import {
  APPOINTMENT_DURATION_OPTIONS,
  PERIOD_LABELS,
  addMinutesToIso,
  dateAndTimeToIso,
  formatSlotDisplay,
  getSlotPeriod,
  isoToTimeInputValue,
  minutesBetweenIso,
  normalizeDurationMinutes,
} from './appointment-create-utils';
import {
  useAppointment,
  useAppointmentQueue,
  useDoctorAvailability,
  useUpdateAppointment,
} from '../hooks/use-appointments.hooks';
import { useOperatoryList } from '../hooks/use-operatories.hooks';
import {
  useProviderAvailabilitySummary,
  useProviderList,
} from '../hooks/use-providers.hooks';
import type { AppointmentDetail } from '../infrastructure/appointments.service';
import type { SlotDto, UpdateAppointmentInput } from '../domain/types';

const RESCHEDULE_REASONS = [
  { value: 'patient_request', label: 'Patient request' },
  { value: 'doctor_unavailable', label: 'Doctor unavailable' },
  { value: 'clinic_adjustment', label: 'Clinic adjustment' },
  { value: 'overbooking', label: 'Overbooking' },
  { value: 'other', label: 'Other' },
] as const;

type RescheduleReason = (typeof RESCHEDULE_REASONS)[number]['value'];

export interface RescheduleAppointmentModalProps {
  appointmentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function resolveDoctorId(detail: AppointmentDetail): string | null {
  return detail.doctorId ?? detail.providerId ?? null;
}

function formatWhen(iso: string): string {
  return format(parseISO(iso), 'EEE, d MMM yyyy · h:mm a');
}

function groupSlots(slots: SlotDto[], timezone?: string): Record<string, SlotDto[]> {
  return slots.reduce<Record<string, SlotDto[]>>((acc, slot) => {
    const period = getSlotPeriod(slot.startAt, timezone);
    if (!acc[period]) acc[period] = [];
    acc[period].push(slot);
    return acc;
  }, {});
}

/**
 * Reschedule popup — same Doctor & Treatment Room controls as create/edit,
 * with a read-only patient strip.
 */
export function RescheduleAppointmentModal({
  appointmentId,
  open,
  onOpenChange,
  onSuccess,
}: RescheduleAppointmentModalProps): JSX.Element {
  const { activeClinic } = useSession();
  const clinicId = activeClinic?.clinicId ?? '';
  const { data: detail, isPending, isError } = useAppointment(
    open && appointmentId ? appointmentId : undefined,
  );
  const { mutate: updateAppt, isPending: isSaving } = useUpdateAppointment();
  const { data: providers } = useProviderList();
  const { data: operatories } = useOperatoryList();
  const { data: clinicProfile } = useClinicProfile();

  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [selectedOperatoryId, setSelectedOperatoryId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [endAt, setEndAt] = useState<string | null>(null);
  const [reason, setReason] = useState<RescheduleReason | ''>('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !detail) return;
    setSelectedProviderId(resolveDoctorId(detail));
    setSelectedOperatoryId(detail.operatoryId);
    setSelectedDate(format(parseISO(detail.scheduledAt), 'yyyy-MM-dd'));
    setDurationMinutes(normalizeDurationMinutes(detail.durationMinutes));
    setSelectedSlot(null);
    setEndAt(null);
    setReason('');
    setNote('');
  }, [open, detail?.id, detail?.scheduledAt, detail?.operatoryId, detail?.doctorId, detail?.providerId, detail?.durationMinutes]);

  const { data: availabilitySummary } = useProviderAvailabilitySummary(selectedDate);
  const { data: slotsData, isLoading: slotsLoading } = useDoctorAvailability(
    clinicId,
    selectedProviderId ?? '',
    selectedDate,
    undefined,
    durationMinutes,
    selectedOperatoryId ?? undefined,
  );
  const { data: dayAppointments } = useAppointmentQueue(selectedDate);

  const slots = slotsData?.slots ?? [];
  const clinicTimezone = slotsData?.timezone ?? clinicProfile?.timezone ?? 'Asia/Kolkata';
  const allowOverbooking = clinicProfile?.allowOverbooking ?? false;
  const selectedProvider = providers?.find((p) => p.id === selectedProviderId);
  const selectedDateObj = new Date(`${selectedDate}T00:00:00`);
  const groupedSlots = useMemo(
    () => groupSlots(slots, clinicTimezone),
    [slots, clinicTimezone],
  );

  /** Other bookings that day; omit the appointment being moved (shown as preview instead). */
  const dayApptsData = useMemo(
    () => (dayAppointments?.data ?? []).filter((a) => a.id !== detail?.id),
    [dayAppointments?.data, detail?.id],
  );

  const startTimeValue = selectedSlot ? isoToTimeInputValue(selectedSlot, clinicTimezone) : '';
  const endTimeValue = endAt ? isoToTimeInputValue(endAt, clinicTimezone) : '';
  const timeRangeLabel =
    selectedSlot && endAt
      ? `${formatSlotDisplay(selectedSlot, clinicTimezone)} – ${formatSlotDisplay(endAt, clinicTimezone)}`
      : selectedSlot
        ? formatSlotDisplay(selectedSlot, clinicTimezone)
        : '';

  const overlappingConflicts = (() => {
    if (!selectedSlot || !endAt) return [];
    const startMs = new Date(selectedSlot).getTime();
    const endMs = new Date(endAt).getTime();
    return dayApptsData.filter((a) => {
      if (selectedProviderId && a.doctorId !== selectedProviderId) return false;
      const aStart = new Date(a.startAt).getTime();
      const aEnd = new Date(a.endAt).getTime();
      return aStart < endMs && aEnd > startMs;
    });
  })();

  const selectedSlotEngaged =
    Boolean(slots.find((s) => s.startAt === selectedSlot)?.isEngaged) ||
    overlappingConflicts.length > 0;

  const isOverbookingSelection = allowOverbooking && selectedSlotEngaged;

  const schedulePreview =
    selectedSlot && endAt
      ? {
          startAt: selectedSlot,
          endAt,
          durationMin: durationMinutes,
          patientName: detail?.patientName ?? undefined,
          isOverbooking: isOverbookingSelection,
        }
      : null;

  const canSubmit =
    !!detail &&
    !!selectedProviderId &&
    !!selectedSlot &&
    (selectedSlot !== detail.scheduledAt ||
      selectedProviderId !== resolveDoctorId(detail) ||
      selectedOperatoryId !== detail.operatoryId ||
      durationMinutes !== detail.durationMinutes) &&
    !isSaving;

  const summaryForProvider = (providerId: string) =>
    availabilitySummary?.find((s) => s.providerId === providerId);

  const statusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'text-mint-600';
      case 'limited':
        return 'text-amber-600';
      default:
        return 'text-ink-500';
    }
  };
  const dotColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-mint-500';
      case 'limited':
        return 'bg-amber-500';
      default:
        return 'bg-ink-400';
    }
  };
  const statusLabel = (summary: { status: string; totalSlots: number }) => {
    if (summary.status === 'off') return 'Off today';
    if (summary.status === 'full') return 'Fully booked';
    if (summary.status === 'limited') {
      return `Limited (${summary.totalSlots} slot${summary.totalSlots !== 1 ? 's' : ''})`;
    }
    return `${summary.totalSlots} slot${summary.totalSlots !== 1 ? 's' : ''} open`;
  };

  const selectSlot = (startIso: string, slotEndIso?: string) => {
    setSelectedSlot(startIso);
    setEndAt(slotEndIso || addMinutesToIso(startIso, durationMinutes));
  };

  const handleDurationChange = (minutes: number) => {
    setDurationMinutes(minutes);
    if (selectedSlot) {
      setEndAt(addMinutesToIso(selectedSlot, minutes));
    }
  };

  const handleStartTimeChange = (timeHm: string) => {
    if (!timeHm) return;
    const startIso = dateAndTimeToIso(selectedDate, timeHm, clinicTimezone);
    if (!startIso) return;
    setSelectedSlot(startIso);
    setEndAt(addMinutesToIso(startIso, durationMinutes));
  };

  const handleEndTimeChange = (timeHm: string) => {
    if (!selectedSlot || !timeHm) return;
    const endIso = dateAndTimeToIso(selectedDate, timeHm, clinicTimezone);
    if (!endIso) return;
    const mins = minutesBetweenIso(selectedSlot, endIso);
    if (mins < 5) return;
    setEndAt(endIso);
    setDurationMinutes(normalizeDurationMinutes(mins));
  };

  const handleSubmit = () => {
    if (!detail || !selectedSlot || !selectedProviderId) return;

    const input: UpdateAppointmentInput = {
      scheduledAt: selectedSlot,
      durationMinutes,
      providerId: selectedProviderId,
      operatoryId: selectedOperatoryId,
      ...(reason ? { rescheduleReason: reason } : {}),
      ...(note.trim() ? { rescheduleNote: note.trim() } : {}),
    };

    updateAppt(
      { id: detail.id, input, version: detail.version },
      {
        onSuccess: () => {
          toast.success('Appointment rescheduled');
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (err: unknown) => {
          if (err instanceof Error) markAsHandled(err);
          const error = err as { message?: string };
          toast.failure(error?.message ?? 'Failed to reschedule appointment');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <DialogHeader className="shrink-0 border-b hairline px-5 pb-4 pt-5 pr-12 text-left">
          <DialogTitle className="flex items-center gap-2.5 font-display text-[18px]">
            <span className="flex size-8 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
              <CalendarClock className="size-4" />
            </span>
            Reschedule appointment
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Move the visit to a new doctor, chair, or time — same controls as booking.
          </DialogDescription>
        </DialogHeader>

        <div className="nice-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isPending && (
            <div className="space-y-3 animate-pulse py-4">
              <div className="h-16 rounded-xl bg-surface-100" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-20 rounded-xl bg-surface-100" />
                <div className="h-20 rounded-xl bg-surface-100" />
              </div>
              <div className="h-40 rounded-xl bg-surface-100" />
            </div>
          )}

          {isError && (
            <p className="py-10 text-center text-sm text-coral-600">
              Could not load this appointment.
            </p>
          )}

          {!isPending && !isError && detail && (
            <div className="space-y-5">
              {/* Read-only patient */}
              <div className="flex items-center gap-3 rounded-xl2 border hairline bg-surface-50/80 px-3.5 py-3">
                <Avatar
                  name={detail.patientName ?? 'Patient'}
                  size="md"
                  shape="rounded"
                  className="ring-2 ring-white shadow-soft"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-[14px] font-semibold text-ink-900">
                      {detail.patientName ?? 'Patient'}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-ink-500 ring-1 ring-surface-200">
                      <User className="size-3" aria-hidden />
                      Locked
                    </span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-ink-500">
                    Currently {formatWhen(detail.scheduledAt)}
                    {detail.operatoryName ? ` · ${detail.operatoryName}` : ''}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="space-y-5 lg:col-span-2">
              {/* Doctor cards */}
              <div>
                <div className="mb-2.5 text-[12px] font-semibold text-ink-900">Doctor</div>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {providers?.map((p) => {
                    const isSelected = selectedProviderId === p.id;
                    const summary = summaryForProvider(p.id);
                    return (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        className={cn('apt-doc-card', isSelected && 'selected')}
                        onClick={() => {
                          setSelectedProviderId(p.id);
                          setSelectedSlot(null);
                          setEndAt(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setSelectedProviderId(p.id);
                            setSelectedSlot(null);
                            setEndAt(null);
                          }
                        }}
                      >
                        <div className="mb-2 flex items-center gap-2.5">
                          <Avatar name={p.displayName} size="sm" shape="rounded" />
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-semibold text-ink-900">
                              {p.displayName}
                            </div>
                            <div className="text-[11px] capitalize text-ink-500">{p.role}</div>
                          </div>
                          <div
                            className={cn(
                              'ml-auto flex size-5 items-center justify-center rounded-full border-2',
                              isSelected ? 'border-brand-500' : 'border-surface-200',
                            )}
                          >
                            {isSelected && <div className="size-2.5 rounded-full bg-brand-500" />}
                          </div>
                        </div>
                        {summary && (
                          <div
                            className={cn(
                              'flex items-center gap-1 text-[11px] font-medium',
                              statusColor(summary.status),
                            )}
                          >
                            <span className={cn('size-1.5 rounded-full', dotColor(summary.status))} />
                            {statusLabel(summary)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Chair selector */}
              <div className="rounded-xl border border-surface-100 bg-surface-50/60 p-3.5">
                <div className="mb-2.5 flex items-center justify-between gap-2">
                  <span className="form-label mb-0">Treatment chair / room</span>
                  {selectedOperatoryId && (
                    <span className="text-[11px] font-medium text-brand-700">
                      {operatories?.find((o) => o.id === selectedOperatoryId)?.name}
                    </span>
                  )}
                </div>
                <div
                  className="grid grid-cols-2 gap-2 sm:grid-cols-4"
                  role="group"
                  aria-label="Treatment chair or room"
                >
                  {operatories?.map((op) => {
                    const isSelected = selectedOperatoryId === op.id;
                    return (
                      <Button
                        key={op.id}
                        type="button"
                        variant="outline"
                        disabled={!op.isActive}
                        onClick={() => {
                          setSelectedOperatoryId(op.isActive ? op.id : null);
                          setSelectedSlot(null);
                          setEndAt(null);
                        }}
                        className={cn(
                          'gap-2 rounded-xl px-3 py-2.5 text-[12px] font-medium',
                          isSelected
                            ? 'border-2 border-brand-500 bg-white font-semibold text-brand-700 shadow-soft'
                            : op.isActive
                              ? 'border-surface-200 bg-white text-ink-700 hover:border-brand-300'
                              : 'cursor-not-allowed border-surface-200 text-ink-500 opacity-50',
                        )}
                      >
                        <svg
                          className="size-3.5 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                          />
                        </svg>
                        <span className="truncate">{op.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Date & availability */}
              <div className="space-y-4 border-t hairline pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-[14px] font-semibold text-ink-900">Date & availability</h3>
                    <p className="mt-0.5 text-[11px] text-ink-500">
                      Choose the day and duration, then pick a slot.
                    </p>
                  </div>
                  {selectedSlot && (
                    <Badge
                      variant="neutral"
                      className="hidden gap-1.5 border-brand-200 bg-brand-50 text-[11px] font-semibold text-brand-700 sm:inline-flex"
                    >
                      <Clock3 className="size-3.5" aria-hidden />
                      {timeRangeLabel}
                    </Badge>
                  )}
                </div>

                <div className="apt-datetime-toolbar">
                  <div>
                    <label className="form-label" htmlFor="reschedule-date">
                      Appointment date
                    </label>
                    <DatePicker
                      id="reschedule-date"
                      value={selectedDate}
                      onChange={(v) => {
                        if (!v) return;
                        setSelectedDate(v);
                        setSelectedSlot(null);
                        setEndAt(null);
                      }}
                      placeholder="Select date"
                      triggerClassName="inline-flex h-11 w-full min-w-0 items-center gap-2 rounded-xl border border-[var(--border-subtle)] bg-white px-3.5 text-left text-sm font-medium text-[var(--text-primary)] shadow-soft transition-colors hover:bg-[var(--bg-surface-muted)]"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="reschedule-duration">
                      Duration
                    </label>
                    <Select
                      value={String(durationMinutes)}
                      onValueChange={(v) => handleDurationChange(Number(v))}
                    >
                      <SelectTrigger id="reschedule-duration" className="h-11 w-full rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_DURATION_OPTIONS.map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} minutes
                          </SelectItem>
                        ))}
                        {!APPOINTMENT_DURATION_OPTIONS.includes(
                          durationMinutes as (typeof APPOINTMENT_DURATION_OPTIONS)[number],
                        ) && (
                          <SelectItem value={String(durationMinutes)}>
                            {durationMinutes} minutes
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="apt-slots-shell">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[12px] font-semibold text-ink-900">Available slots</div>
                      <div className="mt-0.5 text-[11px] text-ink-500">
                        {[
                          selectedProvider?.displayName,
                          format(selectedDateObj, 'EEE, dd MMM'),
                          selectedOperatoryId
                            ? operatories?.find((o) => o.id === selectedOperatoryId)?.name
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'Select a doctor to load slots'}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {allowOverbooking && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200">
                          <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
                          Overbooking on
                        </span>
                      )}
                      {slots.length > 0 && (
                        <span className="rounded-full bg-surface-100 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-ink-600">
                          {slots.filter((s) => !s.isEngaged).length} open
                          {allowOverbooking && slots.some((s) => s.isEngaged)
                            ? ` · ${slots.filter((s) => s.isEngaged).length} engaged`
                            : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {slotsData?.warnings && slotsData.warnings.length > 0 && (
                    <ScheduleAvailabilityWarning
                      warnings={slotsData.warnings}
                      className="mb-3"
                    />
                  )}

                  {slotsLoading && (
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-8 w-20 animate-pulse rounded-lg bg-surface-100" />
                      ))}
                    </div>
                  )}

                  {!slotsLoading && !selectedProviderId && (
                    <div className="rounded-xl bg-surface-50 px-4 py-6 text-center">
                      <p className="text-[13px] font-medium text-ink-700">Select a doctor</p>
                      <p className="mt-1 text-[12px] text-ink-500">
                        Availability appears once a provider is chosen.
                      </p>
                    </div>
                  )}

                  {!slotsLoading && selectedProviderId && slots.length === 0 && (
                    <div className="rounded-xl bg-surface-50 px-4 py-6 text-center">
                      <p className="text-[13px] font-medium text-ink-700">No open slots</p>
                      <p className="mt-1 text-[12px] text-ink-500">
                        Try another date, chair, or shorten the duration.
                      </p>
                    </div>
                  )}

                  {!slotsLoading && slots.length > 0 && (
                    <div className="space-y-3.5">
                      {Object.entries(groupedSlots).map(([period, periodSlots]) => (
                        <div key={period}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                              {PERIOD_LABELS[period]}
                            </span>
                            <span className="h-px flex-1 bg-surface-100" />
                            <span className="text-[10px] tabular-nums text-ink-400">
                              {periodSlots.length}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {periodSlots.map((slot) => {
                              const engaged = Boolean(slot.isEngaged);
                              return (
                                <Button
                                  key={slot.startAt}
                                  type="button"
                                  variant="ghost"
                                  disabled={engaged && !allowOverbooking}
                                  onClick={() => selectSlot(slot.startAt, slot.endAt)}
                                  className={cn(
                                    'apt-slot-btn',
                                    selectedSlot === slot.startAt && 'selected',
                                    engaged && !allowOverbooking && 'unavailable',
                                    engaged && allowOverbooking && 'overbookable',
                                  )}
                                  title={
                                    engaged
                                      ? 'Engaged — booking will overbook this window'
                                      : undefined
                                  }
                                >
                                  {formatSlotDisplay(slot.startAt, clinicTimezone)}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <AppointmentTimeWindow
                  startValue={startTimeValue}
                  endValue={endTimeValue}
                  rangeLabel={timeRangeLabel}
                  durationMinutes={durationMinutes}
                  startDisabled={!selectedProviderId}
                  endDisabled={!selectedSlot}
                  onStartChange={handleStartTimeChange}
                  onEndChange={handleEndTimeChange}
                />
              </div>

              {/* Optional reason */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="form-label" htmlFor="reschedule-reason">
                    Reason (optional)
                  </label>
                  <Select
                    value={reason || undefined}
                    onValueChange={(v) => setReason(v as RescheduleReason)}
                  >
                    <SelectTrigger id="reschedule-reason" className="h-11 rounded-xl">
                      <SelectValue placeholder="Why is this moving?" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESCHEDULE_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="form-label" htmlFor="reschedule-note">
                    Note (optional)
                  </label>
                  <Input
                    id="reschedule-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                    placeholder="Short note for history"
                    className="h-11 rounded-xl shadow-soft"
                  />
                </div>
              </div>
                </div>

                <div className="lg:col-span-1 lg:sticky lg:top-0 self-start">
                  <DayAppointmentsPanel
                    dateLabel={format(selectedDateObj, 'EEEE, dd MMM yyyy')}
                    appointments={dayApptsData}
                    timezone={clinicTimezone}
                    operatories={operatories}
                    preview={schedulePreview}
                    className="max-h-[min(480px,55vh)] min-h-[280px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t hairline bg-white px-5 py-3 sm:justify-between">
          <p className="hidden text-[11px] text-ink-500 sm:block">
            Patient cannot be changed here — use Edit for that.
          </p>
          <div className="flex w-full justify-end gap-2 sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {isSaving ? 'Saving…' : 'Confirm reschedule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
